import React, { useEffect, useLayoutEffect, useState, useRef, useMemo } from 'react';
import VoiceRealtimeBar from '../interview/VoiceRealtimeBar';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { removeAuthCookie } from '../../utils/authUtils';
import {
    getCollectContributorSession,
    postCollectContributorMessage,
    postCollectContributorInvite,
    downloadContributorTranscript,
} from '../../services/api/collectDataService';
import RespondentSidebar from './RespondentSidebar';
import ContributorRightPanel, { type ContributorGuideProgress } from './ContributorRightPanel';
import InterviewContextTopBar from '../interview/InterviewContextTopBar';
import '../../features/process/process/process-create.css';
import '../../styles/interview-redesign.css';
import { renderInlineBoldMd } from '../../utils/renderInlineBoldMd';
import {
    formatInterviewScrollHeader,
    getChatTimestampHoverTitle,
    formatCollectWorkspaceRelativeLabel,
} from '../../utils/formatDate';
import { useContributorUiLocaleSync } from '../../utils/collectLocaleI18n';
import { contributorInitials } from '../../utils/contributorDisplayName';
import { stashCollectInviteReturn } from '../../utils/collectInviteRedirect';
import { Routes } from '../../routes';
import { logout, useAppDispatch, useAppSelector } from '../../stores';
import { InviteUser, type InviteCustomSubmitResult } from '../modal';
import { useInterviewThreadScroll } from '../../hooks/useInterviewThreadScroll';
import { getTenantInitial, getTenantName } from '../../utils/brandingStyles';

type Msg = { id?: string; role: string; content: string; created_at?: string; createdAt?: string };

function isUserRole(role: string) {
    return role === 'user' || role === 'contributor' || role === 'owner';
}

const CollectContributorPage = () => {
    const { token } = useParams<{ token: string }>();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const auth = useAppSelector((s) => s.auth);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [collection, setCollection] = useState<any>(null);
    const [messages, setMessages] = useState<Msg[]>([]);
    const [participants, setParticipants] = useState<any[]>([]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const [status, setStatus] = useState('');
    const [exportLoading, setExportLoading] = useState(false);
    const [assistantTyping, setAssistantTyping] = useState(false);
    const [guideProgress, setGuideProgress] = useState<ContributorGuideProgress | null>(null);
    const [voiceSessionActive, setVoiceSessionActive] = useState(false);
    const [inviteEmailHasFamlaAccount, setInviteEmailHasFamlaAccount] = useState<boolean | undefined>(undefined);
    const [collectionEnded, setCollectionEnded] = useState(false);
    const [collectionEndedReason, setCollectionEndedReason] = useState<'closed' | 'deadline' | null>(null);
    /** Fermeture locale du modal « collecte terminée » (affichage géré en React, sans bootstrap.Modal). */
    const [collectionEndedModalDismissed, setCollectionEndedModalDismissed] = useState(false);
    const [contributionWithdrawn, setContributionWithdrawn] = useState(false);
    const contributorEmailNormRef = useRef<string>('');

    const inviteCloseRef = useRef<HTMLButtonElement>(null);
    const streamRevealTimerRef = useRef<number | null>(null);
    const [chatTimeTick, setChatTimeTick] = useState(0);

    const messageTimestamps = useMemo(
        () => messages.map((m) => m.created_at ?? m.createdAt),
        [messages],
    );

    const { threadScrollRef, scrollHeaderDisplay } = useInterviewThreadScroll({
        messageTimestamps,
        scrollDeps: [messages, assistantTyping, sending, status],
    });

    const chatReferenceNow = useMemo(
        () => new Date(),
        [chatTimeTick, messages, assistantTyping, sending],
    );

    const selfParticipant = useMemo(() => participants.find((p) => p.isSelf) ?? null, [participants]);

    const otherParticipants = useMemo(() => participants.filter((p) => !p.isSelf), [participants]);

    /** Dernière bulle assistant — alignement premier tour vocal (comme process interview). */
    const pendingVoiceQuestion = useMemo(() => {
        for (let i = messages.length - 1; i >= 0; i -= 1) {
            if (!isUserRole(String(messages[i]?.role || ''))) {
                return (messages[i]?.content || '').trim();
            }
        }
        return '';
    }, [messages]);

    useEffect(() => {
        const id = window.setInterval(() => setChatTimeTick((x) => x + 1), 6000);
        return () => clearInterval(id);
    }, []);

    const load = async () => {
        if (!token) {
            setError('Invalid link');
            setLoading(false);
            return;
        }
        try {
            const res = await getCollectContributorSession(token);
            if (res.status !== 200 || !res.body?.data) {
                setError(res.body?.description || 'Failed to load session');
                setLoading(false);
                return;
            }
            const d = res.body.data;
            setInviteEmailHasFamlaAccount(
                typeof d.invite_email_has_famla_account === 'boolean'
                    ? d.invite_email_has_famla_account
                    : undefined,
            );
            setCollection(d.collection);
            const processedParticipants = (d.participants || []).map((p: any) => ({
                ...p,
                initials: contributorInitials(p),
                isSelf: Boolean(p.is_self) || p.id === d.contributor?.id,
            }));

            setParticipants(processedParticipants);
            setMessages(d.messages || []);
            setStatus(d.contributor?.status || '');
            setGuideProgress(d.guide_progress ?? null);
            setCollectionEnded(Boolean(d.collection_ended_for_contributor));
            setCollectionEndedReason(
                d.collection_ended_reason === 'closed' || d.collection_ended_reason === 'deadline'
                    ? d.collection_ended_reason
                    : null,
            );
            contributorEmailNormRef.current = String(d.contributor?.email || '').trim().toLowerCase();
        } catch {
            setError('Connection error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        contributorEmailNormRef.current = '';
        setCollectionEndedModalDismissed(false);
        load();
    }, [token]);

    useEffect(() => {
        if (!collectionEnded) setCollectionEndedModalDismissed(false);
    }, [collectionEnded]);

    useContributorUiLocaleSync(token, collection?.effective_locale);

    /** Invités avec compte Famla : ouvrir la session applicative sur cet e-mail puis revenir sur `/r/:token`. */
    useLayoutEffect(() => {
        if (loading || error) return;
        if (inviteEmailHasFamlaAccount !== true || !token) return;
        const contributorEmail = contributorEmailNormRef.current;
        if (!contributorEmail) return;

        const sessionEmail = String(auth.user?.email || '').trim().toLowerCase();
        const verifiedOk = auth.user?.verified !== false;
        const loggedIn = Boolean(auth.access_token && auth.logged_in && verifiedOk && sessionEmail);

        if (!loggedIn || sessionEmail !== contributorEmail) {
            stashCollectInviteReturn(`/r/${token}`);
            if (loggedIn && sessionEmail !== contributorEmail) {
                removeAuthCookie('access_token');
                removeAuthCookie('refresh_token');
                removeAuthCookie('logged_in');
                dispatch(logout());
            }
            navigate(`${Routes.AUTH_LOGIN}?redirect=${encodeURIComponent(`/r/${token}`)}`, { replace: true });
        }
    }, [
        loading,
        error,
        inviteEmailHasFamlaAccount,
        token,
        auth.access_token,
        auth.logged_in,
        auth.user?.email,
        auth.user?.verified,
        navigate,
        dispatch,
    ]);

    useEffect(
        () => () => {
            if (streamRevealTimerRef.current != null) {
                window.clearInterval(streamRevealTimerRef.current);
                streamRevealTimerRef.current = null;
            }
        },
        [],
    );

    const collectionIsDraft = String(collection?.status ?? '').toLowerCase() === 'draft';
    const contributorCanReply =
        !collectionEnded && status !== 'completed' && !collectionIsDraft;

    const handleSend = async () => {
        if (
            !token ||
            !input.trim() ||
            sending ||
            status === 'completed' ||
            collectionEnded ||
            collectionIsDraft ||
            voiceSessionActive
        )
            return;
        const text = input.trim();
        const pendingId = `local-${Date.now()}`;
        setMessages((m) => [
            ...m,
            { id: pendingId, role: 'user', content: text, created_at: new Date().toISOString() },
        ]);
        setInput('');
        setSending(true);
        setAssistantTyping(true);
        try {
            const res = await postCollectContributorMessage(token, text);
            setAssistantTyping(false);
            if (res.status === 200 && res.body?.data) {
                const st = String(res.body.data.contributor_status || '');
                setStatus(st);
                setParticipants((ps) => ps.map((p) => (p.isSelf ? { ...p, status: st } : p)));
                if (res.body.data.guide_progress != null) {
                    setGuideProgress(res.body.data.guide_progress);
                }
                const full: Msg[] = res.body.data.messages || [];
                let lastAi = -1;
                for (let i = full.length - 1; i >= 0; i--) {
                    if (!isUserRole(full[i].role)) {
                        lastAi = i;
                        break;
                    }
                }
                if (streamRevealTimerRef.current != null) {
                    window.clearInterval(streamRevealTimerRef.current);
                    streamRevealTimerRef.current = null;
                }
                if (lastAi >= 0) {
                    const fullText = full[lastAi].content || '';
                    const tail = full[lastAi];
                    const prefix = full.slice(0, lastAi);
                    if (fullText.length < 28) {
                        setMessages(full);
                    } else {
                        setMessages([...prefix, { ...tail, content: '' }]);
                        let pos = 0;
                        const step = Math.max(2, Math.ceil(fullText.length / 26));
                        streamRevealTimerRef.current = window.setInterval(() => {
                            pos = Math.min(fullText.length, pos + step);
                            setMessages([...prefix, { ...tail, content: fullText.slice(0, pos) }]);
                            if (pos >= fullText.length && streamRevealTimerRef.current != null) {
                                window.clearInterval(streamRevealTimerRef.current);
                                streamRevealTimerRef.current = null;
                                setMessages(full);
                            }
                        }, 26);
                    }
                } else {
                    setMessages(full);
                }
            } else {
                setMessages((m) => m.filter((x) => x.id !== pendingId));
                setInput(text);
            }
        } catch (err) {
            console.error('Failed to send message', err);
            setAssistantTyping(false);
            setMessages((m) => m.filter((x) => x.id !== pendingId));
            setInput(text);
        } finally {
            setSending(false);
        }
    };

    const handleExport = async () => {
        if (!token || exportLoading) return;
        setExportLoading(true);
        try {
            const res = await downloadContributorTranscript(token);
            if (res.ok && res.blob) {
                const url = window.URL.createObjectURL(res.blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `Transcript_${collection?.name || 'Collection'}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
            }
        } catch (err) {
            console.error('Export failed', err);
        } finally {
            setExportLoading(false);
        }
    };

    const onContributorInviteCustomSubmit = async (data: {
        emails: string[];
        names: string[];
        role: string;
    }): Promise<InviteCustomSubmitResult> => {
        if (!token) return { ok: false, message: t('words.error') };
        const roleNorm = (data.role || '').trim();
        if (!roleNorm) {
            return { ok: false, message: t('navigation.enterRole', 'Enter a role') };
        }
        const roleKey = roleNorm.toLowerCase();
        const dupMsg = (emailDisplay: string) =>
            t('form.collectWorkspace.duplicateContributorRole', {
                email: emailDisplay,
                defaultValue: '{{email}} is already invited for this role.',
            });
        const selfEmail = contributorEmailNormRef.current;
        const seenKeys = new Set<string>();
        for (const emailRaw of data.emails) {
            const em = emailRaw.trim().toLowerCase();
            if (!em) continue;
            if (selfEmail && em === selfEmail) {
                return {
                    ok: false,
                    message: t(
                        'form.collectWorkspace.recipientInviteSelfEmail',
                        'You cannot invite your own email address.',
                    ),
                };
            }
            const k = `${em}|${roleKey}`;
            if (seenKeys.has(k)) return { ok: false, message: dupMsg(emailRaw.trim()) };
            seenKeys.add(k);
            const anonList = Boolean(collection?.anonymous_responses);
            const already = anonList
                ? false
                : participants.some((p: { email?: string; role?: string }) => {
                      const ce = String(p.email ?? '').trim().toLowerCase();
                      const cr = String(p.role ?? '').trim().toLowerCase();
                      return ce === em && cr === roleKey;
                  });
            if (already) return { ok: false, message: dupMsg(emailRaw.trim()) };
        }
        let allSuccess = true;
        let lastMessage: string | undefined;
        for (let i = 0; i < data.emails.length; i++) {
            const email = data.emails[i].trim();
            if (!email) continue;
            const name = (data.names[i] || '').trim() || email.split('@')[0];
            try {
                const res = await postCollectContributorInvite(token, { name, email, role: roleNorm });
                if (res.status !== 201) {
                    allSuccess = false;
                    lastMessage =
                        res.body?.description ||
                        t(
                            'form.collectWorkspace.recipientInviteFailed',
                            'Invitation could not be sent. Please try again.',
                        );
                }
            } catch {
                allSuccess = false;
                lastMessage = t('form.collectWorkspace.connectionError', 'Connection error');
            }
        }
        await load();
        if (allSuccess) return { ok: true };
        return { ok: false, message: lastMessage || t('words.error') };
    };

    const selfParticipantRaw = participants.find((p) => p.isSelf);
    const selfInitials = selfParticipantRaw ? contributorInitials(selfParticipantRaw) : '?';

    const statusBadge = (
        <span
            className={`collect-badge ${
                status === 'completed' || collectionEnded
                    ? 'done'
                    : collectionIsDraft
                      ? 'draft'
                      : 'active'
            }`}
        >
            {collectionEnded && status !== 'completed'
                ? t('form.collectWorkspace.collectionEndedBadge', 'Collection ended')
                : status === 'completed'
                  ? t('words.completed', 'Completed')
                  : collectionIsDraft
                    ? t('form.collectWorkspace.collectionBadgeDraft', 'Draft')
                    : t('form.collectWorkspace.liveSession', 'In progress')}
        </span>
    );

    const contributorEmailGate = contributorEmailNormRef.current;
    const sessionEmailGate = String(auth.user?.email || '').trim().toLowerCase();
    const verifiedGateOk = auth.user?.verified !== false;
    const loggedInGate = Boolean(auth.access_token && auth.logged_in && verifiedGateOk && sessionEmailGate);
    const awaitingFamlaAuthRedirect =
        !loading &&
        !error &&
        inviteEmailHasFamlaAccount === true &&
        Boolean(token && contributorEmailGate && (!loggedInGate || sessionEmailGate !== contributorEmailGate));

    if (loading || awaitingFamlaAuthRedirect) {
        return (
            <div className="pc-wrap collect-workspace-root">
                <div className="interview-shell" style={{ flex: 1, minHeight: 0 }}>
                    <div
                        className="shell"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 240,
                        }}
                    >
                        <p className="collect-muted">
                            {loading
                                ? t('form.collectWorkspace.loadingSession', 'Loading your secure session…')
                                : t(
                                      'form.collectWorkspace.redirectingToSignIn',
                                      'Opening sign-in so your {{appName}} account opens this contribution…',
                                  )}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (error && !messages.length) {
        return (
            <div className="pc-wrap collect-workspace-root">
                <div className="interview-shell" style={{ flex: 1, minHeight: 0, padding: 24 }}>
                    <div className="collect-alert danger" role="alert">
                        {error}
                    </div>
                </div>
            </div>
        );
    }

    if (contributionWithdrawn) {
        return (
            <div className="pc-wrap collect-workspace-root">
                <div className="interview-shell" style={{ flex: 1, minHeight: 0, padding: 24 }}>
                    <div className="collect-contrib-thanks" role="status" style={{ maxWidth: 480, margin: '48px auto' }}>
                        <div className="collect-contrib-thanks-icon" aria-hidden>
                            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2">
                                <path d="M20 6L9 17L4 12" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <h2 className="collect-contrib-thanks-title">
                            {t('form.collectWorkspace.withdrawMyContributionDoneTitle', {
                                defaultValue: 'Your contribution has been removed',
                            })}
                        </h2>
                        <p className="collect-contrib-thanks-body">
                            {t('form.collectWorkspace.withdrawMyContributionDoneBody', {
                                defaultValue:
                                    'Your responses and messages for this collection have been deleted. This invitation link is no longer valid.',
                            })}
                        </p>
                        <div className="collect-contrib-thanks-actions">
                            <Link to={Routes.AUTH_SIGNIN} className="action-btn primary">
                                {t('form.collectWorkspace.contributorQuickCreateAccount', 'Create a {{appName}} account')}
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const showCollectionEndedModal = collectionEnded && !collectionEndedModalDismissed;

    return (
        <div className="pc-wrap collect-workspace-root">
            <div className="interview-shell" style={{ flex: 1, minHeight: 0 }}>
                <div className="shell">
                    <RespondentSidebar
                        selfParticipant={selfParticipant}
                        otherParticipants={otherParticipants}
                        inviteEmailHasFamlaAccount={inviteEmailHasFamlaAccount}
                        showAddRecipient={Boolean(collection?.recipients_can_invite && token && !collectionEnded)}
                        activeCollectionId={collection?.id != null ? String(collection.id) : undefined}
                        activeCollectionContributorToken={token}
                        activeCollectionOpenedAsContributor
                        onActiveContributionWithdrawn={() => {
                            const sessionEmail = String(auth.user?.email || '').trim().toLowerCase();
                            const verifiedOk = auth.user?.verified !== false;
                            const loggedIn = Boolean(auth.access_token && auth.logged_in && verifiedOk && sessionEmail);
                            if (loggedIn) {
                                navigate(Routes.HOMEVIEW, { replace: true });
                                return;
                            }
                            setContributionWithdrawn(true);
                        }}
                        activeCollection={
                            collection
                                ? {
                                      name: collection.name,
                                      status: collection.status,
                                      created_at: collection.created_at,
                                      updated_at: collection.updated_at,
                                  }
                                : null
                        }
                    />

                    <div className="centre">
                        <div className="c-topbar">
                            <InterviewContextTopBar
                                processTitle={collection?.name || t('form.dataCollect.processOverview', 'Data collection')}
                                hideCreateCta
                                extraActions={statusBadge}
                                titleAddon={
                                    collection?.anonymous_responses ? (
                                        <span
                                            className="collect-anonymous-title-badge"
                                            title={t(
                                                'form.collectWorkspace.anonymousBadgeTitle',
                                                'Your replies are labeled with a fictitious name for the collection owner.',
                                            )}
                                        >
                                            {t('form.collectWorkspace.anonymousBadge', 'Anonymous')}
                                        </span>
                                    ) : undefined
                                }
                            />
                        </div>

                        <div className="thread" ref={threadScrollRef}>
                            {scrollHeaderDisplay ? (
                                <div className="interview-scroll-date-sticky" aria-live="polite">
                                    {scrollHeaderDisplay}
                                </div>
                            ) : null}
                            {messages.map((m, i) => {
                                const isUser = isUserRole(m.role);
                                const av = isUser ? selfInitials || 'Y' : 'F';
                                const iso = m.created_at ?? m.createdAt;
                                const timeLabel = formatInterviewScrollHeader(iso);
                                const timeTitle = getChatTimestampHoverTitle(iso, chatReferenceNow);
                                return (
                                    <div key={m.id || i} data-msg-timestamp={iso || ''}>
                                        <div className={`turn ${isUser ? 'human human-turn' : 'ai-turn'}`}>
                                            <div className={`t-avatar ${isUser ? 't-human' : 't-ai'}`}>{av}</div>
                                            <div className="t-body">
                                                <div className="t-sender">
                                                    {isUser ? (
                                                        <span className="s-human">
                                                            {t('common.you', 'You')}
                                                            {timeLabel ? (
                                                                <span className="t-time" title={timeTitle}>
                                                                    {timeLabel}
                                                                </span>
                                                            ) : null}
                                                        </span>
                                                    ) : (
                                                        <span className="s-ai">
                                                            {getTenantName()}
                                                            {timeLabel ? (
                                                                <span className="t-time" title={timeTitle}>
                                                                    {timeLabel}
                                                                </span>
                                                            ) : null}
                                                        </span>
                                                    )}
                                                </div>
                                                {isUser ? (
                                                    <div className="bubble-wrap">
                                                        <div className="bubble">{renderInlineBoldMd(m.content)}</div>
                                                    </div>
                                                ) : (
                                                    <div className="bubble">{renderInlineBoldMd(m.content)}</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                            {assistantTyping && (
                                <div className="turn ai-turn" aria-live="polite">
                                    <div className="t-avatar t-ai">{getTenantInitial()}</div>
                                    <div className="t-body">
                                        <div className="t-sender s-ai">
                                            {getTenantName()}
                                            <span
                                                className="t-time"
                                                title={formatInterviewScrollHeader(new Date())}
                                            >
                                                {
                                                    formatCollectWorkspaceRelativeLabel(
                                                        new Date().toISOString(),
                                                        chatReferenceNow,
                                                    ).label
                                                }
                                            </span>
                                        </div>
                                        <div className="collect-typing-bubble">
                                            <span className="collect-typing-dot" />
                                            <span className="collect-typing-dot" />
                                            <span className="collect-typing-dot" />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {status === 'completed' && (
                                <div className="collect-contrib-thanks" role="status">
                                    <div className="collect-contrib-thanks-icon" aria-hidden>
                                        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2">
                                            <path d="M20 6L9 17L4 12" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                    <h2 className="collect-contrib-thanks-title">
                                        {t('form.collectWorkspace.thankYouTitle', 'Thank you for your contribution!')}
                                    </h2>
                                    <p className="collect-contrib-thanks-body">
                                        {t(
                                            'form.collectWorkspace.thankYouBody',
                                            'Your insights have been securely recorded. You can export your transcript from Quick actions.',
                                        )}
                                    </p>
                                    <div className="collect-contrib-thanks-actions">
                                        <button type="button" className="action-btn primary" onClick={handleExport} disabled={exportLoading}>
                                            {exportLoading ? t('words.loading', 'Loading…') : t('form.collectWorkspace.exportTranscript', 'Export my transcript')}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {contributorCanReply ? (
                            <div className="interview-composer-dock">
                                <div className="input-bar">
                                    {token ? (
                                        <VoiceRealtimeBar
                                            collectInvitationToken={token}
                                            disabled={sending}
                                            t={t}
                                            pendingQuestionText={pendingVoiceQuestion}
                                            textDraft={input}
                                            onVoiceSessionChange={setVoiceSessionActive}
                                            onPersistSuccess={() => void load()}
                                        />
                                    ) : null}
                                    <div className="input-inner">
                                        <textarea
                                            className="input-field"
                                            rows={2}
                                            value={input}
                                            onChange={(e) => setInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    void handleSend();
                                                }
                                            }}
                                            placeholder={t('form.collectWorkspace.typeMessage', 'Type your response here…')}
                                            disabled={sending || voiceSessionActive}
                                        />
                                        <div className="input-actions">
                                            <button
                                                type="button"
                                                className="input-btn send-btn"
                                                onClick={() => void handleSend()}
                                                disabled={!input.trim() || sending}
                                                aria-label="Send"
                                                title="Send"
                                            >
                                                <svg viewBox="0 0 16 16" fill="none" aria-hidden>
                                                    <path
                                                        d="M13 8L3 3L5.5 8L3 13L13 8Z"
                                                        stroke="currentColor"
                                                        strokeWidth="1.2"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="interview-input-hints" role="note">
                                        {collection?.anonymous_responses ? (
                                            <>
                                                <span>
                                                    <strong>{t('form.collectWorkspace.privacy', 'Privacy')}:</strong>{' '}
                                                    {t(
                                                        'form.collectWorkspace.privacyTextAnonymousLine1',
                                                        'Your answers are private. The collection owner sees them under a fictitious name only',
                                                    )}
                                                </span>
                                                <span>
                                                    {t(
                                                        'form.collectWorkspace.privacyTextAnonymousLine2',
                                                        'and cannot link that name to you. Other participants cannot see your answers.',
                                                    )}
                                                </span>
                                            </>
                                        ) : (
                                            <span>
                                                <strong>{t('form.collectWorkspace.privacy', 'Privacy')}:</strong>{' '}
                                                {t(
                                                    'form.collectWorkspace.privacyText',
                                                    "This conversation is private between you and the collection owner. You cannot see synthesis or other respondents' answers.",
                                                )}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : collectionIsDraft && status !== 'completed' && !collectionEnded ? (
                            <div className="interview-composer-dock">
                                <div className="interview-input-hints collect-contrib-draft-notice" role="status">
                                    {t(
                                        'form.collectWorkspace.contributorDraftInputDisabled',
                                        'This collection is still being prepared. You will be able to respond once the owner activates it.',
                                    )}
                                </div>
                            </div>
                        ) : null}
                    </div>

                    <ContributorRightPanel
                        guideProgress={guideProgress}
                        exportLoading={exportLoading}
                        onExport={handleExport}
                        inviteEmailHasFamlaAccount={inviteEmailHasFamlaAccount}
                    />

                    <InviteUser
                        processId={collection?.id ?? token ?? ''}
                        closeRef={inviteCloseRef}
                        interviewwId={collection?.id}
                        invited={undefined}
                        onCustomSubmit={onContributorInviteCustomSubmit}
                    />

                    {showCollectionEndedModal ? (
                        <>
                            <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} aria-hidden="true" />
                            <div
                                className="modal fade show d-block"
                                id="collectContributorCollectionEndedModal"
                                tabIndex={-1}
                                role="dialog"
                                aria-modal="true"
                                aria-labelledby="collectContributorCollectionEndedModalLabel"
                                style={{ zIndex: 1055 }}
                            >
                                <div className="modal-dialog modal-dialog-centered">
                                    <div className="modal-content">
                                        <div className="modal-header">
                                            <h5 className="modal-title" id="collectContributorCollectionEndedModalLabel">
                                                {t(
                                                    'form.collectWorkspace.collectionEndedModalTitle',
                                                    'This collection has ended',
                                                )}
                                            </h5>
                                            <button
                                                type="button"
                                                className="btn-close"
                                                aria-label={t('words.close', 'Close')}
                                                onClick={() => setCollectionEndedModalDismissed(true)}
                                            />
                                        </div>
                                        <div className="modal-body">
                                            {collectionEndedReason === 'deadline'
                                                ? t(
                                                      'form.collectWorkspace.collectionEndedModalBodyDeadline',
                                                      'The deadline for this collection has passed. You can still read your conversation above and export your transcript from Quick actions.',
                                                  )
                                                : t(
                                                      'form.collectWorkspace.collectionEndedModalBodyClosed',
                                                      'The owner has closed this collection. You can still read your conversation above and export your transcript from Quick actions.',
                                                  )}
                                        </div>
                                        <div className="modal-footer">
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={() => setCollectionEndedModalDismissed(true)}
                                            >
                                                {t('form.collectWorkspace.collectionEndedModalOk', 'OK')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default CollectContributorPage;
